'use client';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getOwnerBuildings,
  getContactResponses,
  getContactRatings,
  getContactReviews,
  updateContactOpenData,
  getContactBuildingSlugs,
  updateBuildingOpenData,
  getOwner,
  type OwnerDetail,
  type BuildingAggResponse,
  type Building,
  type ReviewApi,
  type BuildingListingsResponse,
} from '@repo/api';
import { useStore } from '@/providers/StoreProvider';

export interface OwnerContextValue {
  owner: OwnerDetail;
  ownerReviews: { data: ReviewApi[]; count: number; next: string | null };
  ownerRatings: Record<string, number> | null;
  aggResponse: BuildingAggResponse | null;
  buildings: Building[];
  isMasked: boolean;
  loading: boolean;
  error: string | null;
  // Pagination state
  fetchingMoreReviews: boolean;
  fetchMoreReviews: () => Promise<void>;
}

export interface OwnerProviderProps {
  children: React.ReactNode;
  owner: OwnerDetail;
  initialBuildings?: Building[];
  initialListings?: BuildingListingsResponse | null;
}

// Create the context
export const OwnerContext = React.createContext<OwnerContextValue | null>(null);

export function OwnerProvider({ children, owner, initialBuildings = [] }: OwnerProviderProps) {
  const explorerData = useStore(state => state.explorer.data);
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const sessionHydrated = useStore(state => state.webAuth.sessionHydrated);
  const fetchExplorerData = useStore(state => state.explorer.fetchExplorerData);
  const authToken = useStore(state => state.webAuth.token);
  const isMasked = useStore(state => state.profile.isBlurRequired);

  const [ownerState, setOwnerState] = useState<OwnerDetail>(owner);
  const [ownerReviews, setOwnerReviews] = useState<{
    data: ReviewApi[];
    count: number;
    next: string | null;
  }>({
    data: owner.seoReviews || [],
    count: owner.reviewCount || 0,
    next: null,
  });
  const [aggResponse, setAggResponse] = useState<BuildingAggResponse | null>(null);
  const [ownerRatings, setOwnerRatings] = useState<Record<string, number> | null>(null);
  const [buildings, setBuildings] = useState<Building[]>(initialBuildings);
  const [loading, setLoading] = useState(false); // Start with false since we have initial data
  const [error, setError] = useState<string | null>(null);
  const [fetchingMoreReviews, setFetchingMoreReviews] = useState(false);
  const [pageReview, setPageReview] = useState(1);
  const isFetchingReviewRef = useRef(false);

  const openDataCheckedContactRef = useRef<string | null>(null);

  /**
   * After owner is fetched, trigger an OpenData refresh once.
   * Flow: Get building slugs -> Update each building's open data -> Update contact's open data
   * If backend says data was updated, refetch owner and replace state.
   * Matches the pattern from v2 website.
   */
  const refreshOwnerFromOpenDataIfNeeded = useCallback(async (ownerData: OwnerDetail) => {
    const city = ownerData?.city === 'New York' ? 'nyc' : ownerData?.city?.toLowerCase();
    const contactId = ownerData?.id;

    if (!contactId) {
      return;
    }
    if (city !== 'nyc') {
      return;
    }
    if (openDataCheckedContactRef.current === contactId) {
      return;
    }

    openDataCheckedContactRef.current = contactId;

    try {
      // Step 1: Get building slugs for this contact
      const buildingSlugs = await getContactBuildingSlugs(contactId);

      // Step 2: Update open data for each building (if we have building slugs)
      if (buildingSlugs && buildingSlugs.length > 0) {
        await Promise.all(
          buildingSlugs.map(async (slug: string) => {
            try {
              await updateBuildingOpenData(slug);
            } catch (err) {
              console.error(`Error updating building open data for ${slug}:`, err);
            }
          }),
        );
      }

      // Step 3: Update contact's open data
      const resp = await updateContactOpenData(contactId);
      if (resp?.updated) {
        const freshOwner = await getOwner(ownerData.id);
        setOwnerState(freshOwner);
      }
    } catch (err) {
      console.error('Error updating contact open data:', err);
    }
  }, []);

  // Fetch contact reviews paginated (matching legacy getContactBuildingReviewsPaginated)
  const fetchReviewsPaginated = useCallback(
    async (page?: number, contactId?: string) => {
      if (isFetchingReviewRef.current) return;
      isFetchingReviewRef.current = true;
      setFetchingMoreReviews(true);

      try {
        const data = await getContactReviews(
          contactId || ownerState.id,
          authToken || undefined,
          page ?? pageReview,
          5, // page_size=5 matching legacy
        );

        if (page === 1) {
          setOwnerReviews({
            data: data.results || [],
            count: data.count || 0,
            next: data.next || null,
          });
          setPageReview(1);
        } else {
          setOwnerReviews(prev => ({
            data: [...prev.data, ...(data.results || [])],
            count: data.count || prev.count,
            next: data.next || null,
          }));
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
      } finally {
        setFetchingMoreReviews(false);
        isFetchingReviewRef.current = false;
      }
    },
    [ownerState.id, authToken, pageReview],
  );

  const fetchMoreReviews = useCallback(async () => {
    if (!ownerReviews.next || fetchingMoreReviews || isFetchingReviewRef.current) return;
    const nextPage = pageReview + 1;
    setPageReview(nextPage);
    await fetchReviewsPaginated(nextPage);
  }, [ownerReviews.next, fetchingMoreReviews, pageReview, fetchReviewsPaginated]);

  const fetchOwnerRelatedData = useCallback(async () => {
    const { id } = ownerState;
    try {
      setLoading(true);
      setError(null);

      // Buildings are public — always fetch (or reuse SSR-provided data).
      const buildingsData =
        initialBuildings.length > 0
          ? { results: initialBuildings, count: initialBuildings.length, next: null }
          : await getOwnerBuildings(id, { page: 1, pageSize: 6 });

      if (initialBuildings.length === 0) {
        setBuildings(buildingsData.results || []);
      }

      // Protected data: bail out entirely when logged out (mirrors the app's
      // fetchProtectedInfo guard). `isMasked` alone isn't enough because for
      // logged-out users the profile slice keeps its initial `isBlurRequired: false`.
      if (!id || !isLoggedIn) return;

      const [aggResponseData, ratingsData, reviewsData] = await Promise.all([
        !isMasked
          ? getContactResponses(id, authToken || undefined).catch(() => null)
          : Promise.resolve<BuildingAggResponse | null>(null),
        !isMasked
          ? getContactRatings(id, authToken || undefined).catch(() => null)
          : Promise.resolve<Record<string, number> | null>(null),
        !isMasked
          ? getContactReviews(id, authToken || undefined, 1, 5).catch(() => ({
              results: [] as ReviewApi[],
              count: 0,
              next: null as string | null,
            }))
          : Promise.resolve<{ results: ReviewApi[]; count: number; next: string | null }>({
              results: [],
              count: 0,
              next: null,
            }),
      ]);

      // Only set other data if we have access (matching legacy behavior)
      if (!isMasked) {
        setOwnerReviews({
          data: reviewsData.results || [],
          count: reviewsData.count || 0,
          next: reviewsData.next || null,
        });
        setAggResponse(aggResponseData);
        setOwnerRatings(ratingsData);
      }
    } catch (err) {
      console.error('Error fetching owner details:', err);
      setError('Failed to load owner details');
      // Don't clear buildings on error, keep what we have
    } finally {
      setLoading(false);
    }
  }, [ownerState, authToken, initialBuildings, isMasked, isLoggedIn]);

  // Sync owner prop with state when it changes
  useEffect(() => {
    setOwnerState(owner);
    // Reset the ref when owner prop changes (new contact)
    openDataCheckedContactRef.current = null;
  }, [owner]);

  // Refresh owner from open data after initial load (matching BuildingProvider pattern)
  useEffect(() => {
    if (ownerState) {
      void refreshOwnerFromOpenDataIfNeeded(ownerState);
    }
  }, [ownerState, refreshOwnerFromOpenDataIfNeeded]);

  useEffect(() => {
    if (sessionHydrated && isLoggedIn && !explorerData) {
      void fetchExplorerData();
    }
  }, [isLoggedIn, fetchExplorerData, explorerData, sessionHydrated]);

  useEffect(() => {
    // Fetch owner-related data (matching legacy fetchContactOtherData)
    // Legacy checks: explorerData?.accessType OR reviewerData?.hasReviewerAccess

    if (!isMasked) {
      void fetchOwnerRelatedData();
    } else {
      // Still fetch buildings if we don't have initial data
      if (initialBuildings.length === 0) {
        void fetchOwnerRelatedData();
      }
    }
  }, [fetchOwnerRelatedData, explorerData?.accessType, initialBuildings.length, isMasked]);

  // Scroll handler for infinite scroll reviews (matching legacy behavior)
  useEffect(() => {
    const handleScroll = () => {
      const lastCard = document.querySelector('.reviewcard:last-child');
      if (lastCard) {
        const rect = lastCard.getBoundingClientRect();
        if (
          rect.bottom <= window.innerHeight &&
          !isFetchingReviewRef.current &&
          ownerReviews.next &&
          !fetchingMoreReviews
        ) {
          void fetchMoreReviews();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [ownerReviews.next, fetchingMoreReviews, fetchMoreReviews]);

  const contextValue: OwnerContextValue = {
    owner: ownerState,
    ownerReviews,
    ownerRatings,
    aggResponse,
    buildings,
    isMasked,
    loading,
    error,
    fetchingMoreReviews,
    fetchMoreReviews,
  };

  return <OwnerContext.Provider value={contextValue}>{children}</OwnerContext.Provider>;
}

// Custom hook for accessing owner data (use this in client components)
export function useOwner(): OwnerContextValue {
  const context = React.useContext(OwnerContext);
  if (!context) {
    throw new Error('useOwner must be used within an OwnerProvider');
  }
  return context;
}
